import React, { useEffect, useState } from 'react';
import { hqPlanService, HqPlan, HqFeature } from '../services/hqPlanService';

const emptyPlan: HqPlan = {
  id: '', code: '', name: '', monthly_price: 0, trial_days: 7,
  alimtalk_unit_price: 13, free_alimtalk_credits: 50, is_active: true,
  display_order: 0, features: [],
};

const HqPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<HqPlan[]>([]);
  const [catalog, setCatalog] = useState<HqFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<HqPlan | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await hqPlanService.list();
      setPlans(res.plans);
      setCatalog(res.catalog);
    } catch (e: any) {
      setError(e?.response?.data?.detail || '플랜을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (p: HqPlan) => { setEditing({ ...p }); setIsNew(false); };
  const openNew = () => { setEditing({ ...emptyPlan }); setIsNew(true); };

  const toggleFeature = (key: string) => {
    if (!editing) return;
    const has = editing.features.includes(key);
    setEditing({
      ...editing,
      features: has ? editing.features.filter((f) => f !== key) : [...editing.features, key],
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.code.trim()) { alert('플랜 코드와 이름은 필수입니다.'); return; }
    setSaving(true);
    try {
      if (isNew) await hqPlanService.create(editing);
      else await hqPlanService.update(editing.id, editing);
      setEditing(null);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const num = (v: number) => (v ?? 0).toLocaleString();

  return (
    <div className="sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl font-semibold text-slate-800">구독 플랜 관리</h1>
        <button onClick={openNew} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">+ 플랜 추가</button>
      </div>

      {loading && <div className="text-slate-400">불러오는 중…</div>}
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-xl p-4 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-semibold text-slate-800">{p.name}</span>
                <span className="ml-2 text-xs text-slate-400">{p.code}</span>
                {!p.is_active && <span className="ml-2 text-xs text-red-500">비활성</span>}
              </div>
              <button onClick={() => openEdit(p)} className="text-blue-600 text-sm hover:underline">수정</button>
            </div>
            <div className="text-sm text-slate-600 space-y-1">
              <div>월 구독료: <b>{num(p.monthly_price)}원</b> {p.monthly_price === 0 && <span className="text-slate-400">(미정)</span>}</div>
              <div>무료체험: {p.trial_days}일 · 카톡 단가: {p.alimtalk_unit_price}원/건 · 무료 {p.free_alimtalk_credits}건</div>
              <div className="text-xs text-slate-500 mt-1">기능 {p.features.length}개</div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">{isNew ? '플랜 추가' : '플랜 수정'}</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="코드(영문)" value={editing.code} disabled={!isNew} onChange={(v) => setEditing({ ...editing, code: v })} />
                <Field label="이름" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NumField label="월 구독료(원)" value={editing.monthly_price} onChange={(v) => setEditing({ ...editing, monthly_price: v })} />
                <NumField label="무료체험(일)" value={editing.trial_days} onChange={(v) => setEditing({ ...editing, trial_days: v })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NumField label="카톡 단가(원/건)" value={editing.alimtalk_unit_price} onChange={(v) => setEditing({ ...editing, alimtalk_unit_price: v })} />
                <NumField label="가입 무료 카톡(건)" value={editing.free_alimtalk_credits} onChange={(v) => setEditing({ ...editing, free_alimtalk_credits: v })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                활성 (점주에게 노출)
              </label>

              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">포함 기능</div>
                <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto border border-slate-100 rounded-lg p-2">
                  {catalog.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm py-0.5">
                      <input type="checkbox" checked={editing.features.includes(f.key)} onChange={() => toggleFeature(f.key)} />
                      {f.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg" disabled={saving}>취소</button>
              <button onClick={save} className="px-4 py-2 bg-slate-800 text-white rounded-lg" disabled={saving}>{saving ? '저장 중…' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; disabled?: boolean; onChange: (v: string) => void }> = ({ label, value, disabled, onChange }) => (
  <div>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    <input type="text" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm disabled:bg-slate-100" />
  </div>
);

const NumField: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    <input type="number" value={String(value)} onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
  </div>
);

export default HqPlansPage;
